import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { OpportunitySendReceipt } from '~/features/shared/createOpportunitiesMachine';
import {
  MarketContentPanel,
  MarketPanelHeader,
  MarketStatusPill,
} from '~/features/shared/market-content';
import { MessageComposer } from '~/features/shared/market-content/MessageComposer';
import type { B2BMessage, B2BOpportunity } from '../types';
import { MessagesSection } from './MessagesSection';

type Props = {
  opportunity: B2BOpportunity;
  messages: B2BMessage[];
  messagesLoading: boolean;
  sendingMessage: boolean;
  sendReceipt?: OpportunitySendReceipt | null;
  initialDraft?: string;
  onDraftChange?: (messageBody: string) => void;
  /** A refused reply for this response, shown in the composer in place of the privacy note. */
  replyError?: string | null;
  actionsDisabled?: boolean;
  currentUserInfoId: number | undefined;
  onSendMessage: (messageBody: string) => void;
};

const messageFormSchema = z.object({
  messageBody: z.string().trim().min(1),
});
type MessageFormValues = z.infer<typeof messageFormSchema>;

export const ResponseDetail = ({
  opportunity,
  messages,
  messagesLoading,
  sendingMessage,
  sendReceipt,
  initialDraft = '',
  onDraftChange,
  replyError = null,
  actionsDisabled = false,
  currentUserInfoId,
  onSendMessage,
}: Props) => {
  const submittedText = useRef<string | null>(null);
  const previousReceipt = useRef(sendReceipt);
  const { listingOwner } = opportunity;

  const { handleSubmit, reset, watch, getValues, setValue } = useForm<MessageFormValues>({
    resolver: zodResolver(messageFormSchema),
    defaultValues: { messageBody: initialDraft },
    mode: 'onChange',
  });
  const messageBody = watch('messageBody') ?? '';

  useEffect(() => {
    if (
      sendReceipt !== previousReceipt.current &&
      sendReceipt?.responseId === opportunity.responseId
    ) {
      if (submittedText.current !== null && getValues('messageBody') === submittedText.current)
        reset({ messageBody: '' });
      submittedText.current = null;
    }
    previousReceipt.current = sendReceipt;
  }, [sendReceipt, opportunity.responseId, getValues, reset]);

  const onSubmit = (values: MessageFormValues) => {
    const trimmed = values.messageBody.trim();
    if (!trimmed || sendingMessage || actionsDisabled) return;
    submittedText.current = getValues('messageBody');
    onSendMessage(trimmed);
  };

  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      variant="scroll"
    >
      <div className="border-b border-grey-900 p-4 text-left sm:p-5">
        <div className="flex items-center gap-4">
          <div
            className={classNames(
              'flex h-10 w-10 items-center justify-center rounded-md text-sm font-bold tracking-wide text-white',
              listingOwner.masked ? 'bg-grey-500' : 'bg-primary-t-50'
            )}
            data-cy="listing-owner-avatar"
          >
            {listingOwner.masked ? 'ID' : listingOwner.displayName.charAt(0)}
          </div>
          <div>
            <h3 className="body-text-sb" data-cy="listing-owner-name">
              {listingOwner.displayName}
            </h3>
            <MarketStatusPill
              status={listingOwner.masked ? 'masked' : 'unmasked'}
              label={listingOwner.masked ? 'Identity masked' : 'Listing owner'}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="px-4 pt-4 pb-2">
          <MarketPanelHeader title={`Messages (${messages.length})`} />
        </div>
        <MessagesSection
          messages={messages}
          currentUserInfoId={currentUserInfoId}
          loading={messagesLoading}
        />
      </div>

      <MessageComposer
        value={messageBody}
        onChange={(text) => {
          setValue('messageBody', text, { shouldValidate: true });
          onDraftChange?.(text);
        }}
        onSend={() => void handleSubmit(onSubmit)()}
        disabled={sendingMessage || actionsDisabled}
        sending={sendingMessage}
        masked={listingOwner.masked}
        error={replyError}
        label="Message to listing owner"
        sendLabel="Send"
        inputTag="message-input"
        sendTag="btn-send-message"
      />
    </MarketContentPanel>
  );
};
